import {useState} from 'react';
import type {RequirementOf} from '../../lib/types';
import {isClientFillable} from '../../lib/client-fillable';
import {resolveFillMode} from '../../lib/fill-mode';
import {SignaturePad} from '../SignaturePad';
import {useFillSurface} from '../FillSurface';
import {useControlFrame} from './control-frame';
import {SlotMarks} from '../SlotMarks';
import {normalizeSignature, type ElemProps, type SignatureValue} from './element-props';
import {buildBaseStyle} from './element-styles';

/**
 * The height one line under the pad occupies, whatever is drawn in it.
 *
 * The signer name is a dashed line in the editor and in the printed report, and
 * a `<select>` (or an `<input>`, for a client) on the fill tab -- and a control
 * carries its own padding and border, so it stood ~7px taller than the line it
 * replaces. That is **two heights for one field**, the failure this file's own
 * comment describes one paragraph down, surviving between the design surface and
 * the fill surface: an author put a signature exactly at the page edge, the
 * editor drew it fitting, and the job's report tab pushed "Signed at" half off
 * the page.
 *
 * Fixed here rather than by growing the line to match the control, because the
 * line is what PRINTS: every template already laid out was designed against it,
 * so making the document taller would clip the PDF for all of them to unclip one
 * screen. The control comes down to the line instead, and the page paginates the
 * same on all three surfaces -- which is the invariant, not a nicety.
 *
 * 14px, the natural height of the 9px dashed row it is measured against, stated
 * rather than inherited so the two cannot drift apart again silently.
 */
const SIGNER_ROW_H = 14;

export function SignatureRenderer({
  item,
  fillMode,
  fillValue,
  onFillChange,
  fillReadOnly,
  fillLocked,
  spanWidth,
}: ElemProps & {item: RequirementOf<'signature'>}) {
  // Surface-level, not per-slot: every signature on one document offers the
  // same roster, and the public link offers none because its signer is in no
  // directory the app can list.
  const {signerOptions, defaultSignerName} = useFillSurface();
  const frame = useControlFrame();
  const s = item.style;
  const style = buildBaseStyle(s);
  const cfg = item.config;
  // Same rule as a cell photo: an unsized cell signature is sized by its COLUMN,
  // which is what the editor draws. Falling through to the page-scale 160 put a
  // signature wider than most columns inside one. See `spanWidth`.
  const w = spanWidth ? '100%' : (cfg.width ?? 160);
  const h = cfg.height ?? 64;
  const placeholder = cfg.placeholder || (fillMode ? 'Sign here' : 'Signature');
  const showName = cfg.show_signer_name ?? false;
  const showAt = cfg.show_signed_at ?? false;
  const required = cfg.required ?? item.required;
  const clientOwned = isClientFillable(cfg);

  // The signature is interactive only while the editor's "Edit"/"Editing"
  // (fill-mode) toggle is on — outside it the pad is inert. When wired to a
  // real job fill (`onFillChange`) it captures the answer; in the editor's
  // preview toggle there's no persistence, so the drawing lives in throwaway
  // local state and is NEVER written back to the template (mirrors how the
  // text renderer becomes an uncontrolled input in the same mode).
  const mode = resolveFillMode({
    fillMode,
    hasOnChange: !!onFillChange,
    fillReadOnly: !!fillReadOnly,
  });
  const controlled = mode === 'controlled';
  const previewInteractive = mode === 'preview';
  const interactive = controlled || previewInteractive;
  const options = signerOptions;

  const [localSig, setLocalSig] = useState<SignatureValue>({image: ''});
  const sig = previewInteractive ? localSig : normalizeSignature(fillValue);

  // Drawing a signature stamps the capture time; clearing resets the field.
  //
  // The next value is built ONCE and then dispatched, rather than per branch:
  // the two branches differ only in where the value goes (throwaway local state
  // in the editor's preview, the answer map in a real fill), and writing the
  // literal twice is how the two drifted apart in the first place.
  const onDraw = (dataUrl: string) => {
    // Attribute the signature to whoever drew it, unless a signer was already
    // chosen. Signing is an act by the person at the screen, so making them
    // hunt for their own name in a dropdown afterwards is asking them to
    // restate what the app already knows.
    const next: SignatureValue | undefined = dataUrl
      ? {
          ...sig,
          signer_name: sig.signer_name ?? defaultSignerName,
          image: dataUrl,
          signed_at: new Date().toISOString(),
        }
      : undefined;
    if (previewInteractive) {
      setLocalSig(next ?? {image: ''});
      return;
    }
    onFillChange?.(next);
  };
  const onPickSigner = (name: string) => {
    if (previewInteractive) {
      setLocalSig({...sig, signer_name: name || undefined});
      return;
    }
    onFillChange?.({...sig, signer_name: name || undefined});
  };

  const signedAtLabel = sig.signed_at ? new Date(sig.signed_at).toLocaleString() : '';

  return (
    // `inline-block` shrink-wraps, which makes a `100%` child circular -- it
    // resolves against the shrink-to-fit width rather than the cell's. Spanning
    // therefore has to change the wrapper too, and only the spanning case does:
    // a standalone signature sits inline on a page exactly as before.
    <div style={style} className={`relative ${spanWidth ? 'block w-full' : 'inline-block'}`}>
      <SlotMarks required={required} config={cfg} />
      {/* **No caption above the pad.** A signature in a table cell never drew
          one, so the same element read two ways depending on where it sat, and
          on a page-designed report every pad carried the word "Signature" over
          a box that already looks like exactly one thing. A caption that says
          what the pad says is noise on a document someone signs.

          An author who wants one — "Client", "Technician" — puts a text element
          above the pad, which is what the cell path has always required, and
          the `Label` control is gone from the signature toolbar rather than
          left offering a field that renders nowhere. */}
      {fillReadOnly ? (
        <div
          style={{width: w, height: h}}
          className={`flex items-center justify-center overflow-hidden rounded bg-white ${frame('open')}`}
        >
          {sig.image ? (
            <img src={sig.image} alt="Signature" className="h-full w-full object-contain" />
          ) : (
            <span className="text-[10px] text-gray-300">Not signed</span>
          )}
        </div>
      ) : interactive ? (
        <div style={{width: w, height: h}}>
          <SignaturePad value={sig.image || undefined} onChange={onDraw} />
        </div>
      ) : (
        <div
          style={{width: w, height: h}}
          className={`flex items-center justify-center rounded text-[10px] text-gray-300 ${frame('pad')}`}
        >
          {placeholder}
        </div>
      )}

      {/* Signer name + signed-at. In fill mode the name is chosen from the job's
          assignees and the time is auto-stamped when the signature is drawn. */}
      {(showName || showAt) && (
        <div className="mt-1 space-y-1" style={{width: w}}>
          {showName &&
            /**
             * The control, whoever is looking. It used to draw only when THIS
             * viewer could type into it, so a client-owned name was an input on
             * the signing link and a dashed line on the job — two heights for
             * one field, and the same JAC paginated differently on the two
             * screens. What the field IS does not depend on who is looking; only
             * a settled document turns it into a line to be read, which is
             * `fillReadOnly` and not `fillLocked`.
             */
            (interactive || fillLocked ? (
              // Who is signing decides the control (DERP-247). A CLIENT is in no
              // directory the app can offer, so their name is free text — a
              // dropdown of crew names left them unable to give their own at all.
              // A CREW signature keeps the picker: the names are known, and
              // choosing beats typing when the value has to match a real person.
              clientOwned ? (
                <input
                  value={sig.signer_name ?? ''}
                  onChange={(e) => onPickSigner(e.target.value)}
                  placeholder="Your name"
                  readOnly={!interactive}
                  // `SIGNER_ROW_H` with no vertical padding: the control occupies
                  // exactly the row the dashed line does. See the constant.
                  style={{height: SIGNER_ROW_H}}
                  className={`w-full rounded px-1 py-0 text-[10px] leading-none ${frame('open')}`}
                />
              ) : (
                <select
                  value={sig.signer_name ?? ''}
                  onChange={(e) => onPickSigner(e.target.value)}
                  disabled={!interactive}
                  // `appearance-none` with the frame: a native select still
                  // draws its own arrow, which is the app asking a question on a
                  // page nobody can answer it on.
                  style={{height: SIGNER_ROW_H}}
                  className={`w-full rounded px-1 py-0 text-[10px] leading-none ${frame('select')}`}
                >
                  <option value="">Select signer…</option>
                  {options.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              )
            ) : (
              <div
                style={{height: SIGNER_ROW_H}}
                className="flex items-end gap-1 text-[9px] text-gray-500"
              >
                <span className="shrink-0">Name</span>
                <span className="flex-1 border-b border-dashed border-gray-300 text-gray-700">
                  {fillReadOnly ? (sig.signer_name ?? '') : ''}
                </span>
              </div>
            ))}
          {showAt && (
            <div
              style={{height: SIGNER_ROW_H}}
              className="flex items-end gap-1 text-[9px] text-gray-500"
            >
              <span className="shrink-0">Signed at</span>
              <span className="flex-1 border-b border-dashed border-gray-300 text-gray-700">
                {interactive || fillReadOnly ? signedAtLabel : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
